/**
 * Hook para verificar se um monitor tem permissão para acessar avaliações
 * Um monitor tem permissão se estiver vinculado a pelo menos uma atividade pedagógica (qualquer uma)
 * OU a pelo menos uma atividade esportiva
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "./useAuth";
import { useTurmas } from "./useTurmas";
import { DISCIPLINAS } from "./useAvaliacoes";
import { ROLES } from "../constants/roles";
import { ATIVIDADES_ESPORTIVAS } from "../constants/atividadesEsportivas";
import { ATIVIDADES_PEDAGOGICAS } from "../constants/atividadesPedagogicas";
import { atividadesEsportivasService, atividadesPedagogicasService } from "../services";

/**
 * Verifica se uma atividade é esportiva baseado no ID
 * Verifica primeiro na constante, depois pode buscar do banco se necessário
 * @param {string} atividadeId - ID da atividade
 * @param {Array} atividadesEsportivasCache - Cache de atividades esportivas do banco (opcional)
 * @returns {boolean}
 */
const isAtividadeEsportiva = (atividadeId, atividadesEsportivasCache = []) => {
  if (!atividadeId) return false;
  const atividadeIdUpper = String(atividadeId).toUpperCase();

  // Verifica se está na lista de atividades esportivas conhecidas (constante)
  if (Object.keys(ATIVIDADES_ESPORTIVAS).some(key => key === atividadeIdUpper)) {
    return true;
  }

  // Verifica no cache do banco de dados
  if (Array.isArray(atividadesEsportivasCache) && atividadesEsportivasCache.length > 0) {
    return atividadesEsportivasCache.some(
      atividade => String(atividade.id || atividade.atividade_id).toUpperCase() === atividadeIdUpper
    );
  }

  return false;
};

/**
 * Verifica se uma atividade é pedagógica baseado no ID
 * Verifica primeiro na constante, depois pode buscar do banco se necessário
 * @param {string} atividadeId - ID da atividade
 * @param {Array} atividadesPedagogicasCache - Cache de atividades pedagógicas do banco (opcional)
 * @returns {boolean}
 */
const isAtividadePedagogica = (atividadeId, atividadesPedagogicasCache = []) => {
  if (!atividadeId) return false;
  const atividadeIdUpper = String(atividadeId).toUpperCase();

  // Verifica se está na lista de atividades pedagógicas conhecidas (constante)
  const isInConstant = Object.keys(ATIVIDADES_PEDAGOGICAS).some(key => key === atividadeIdUpper);
  if (isInConstant) {
    if (process.env.NODE_ENV === 'development') {
    }
    return true;
  }

  // Verifica no cache do banco de dados
  if (Array.isArray(atividadesPedagogicasCache) && atividadesPedagogicasCache.length > 0) {
    const isInCache = atividadesPedagogicasCache.some(
      atividade => String(atividade.id || atividade.atividade_id).toUpperCase() === atividadeIdUpper
    );
    if (isInCache) {
      return true;
    }
  }

  return false;
};

/**
 * Verifica se o monitor está vinculado a atividades pedagógicas OU esportivas nas turmas
 * @param {Object} user - Usuário logado
 * @param {Array} turmas - Lista de turmas do monitor
 * @param {Array} atividadesEsportivasCache - Cache de atividades esportivas do banco (opcional)
 * @param {Array} atividadesPedagogicasCache - Cache de atividades pedagógicas do banco (opcional)
 * @returns {boolean}
 */
const verificarVinculacaoAtividade = (user, turmas, atividadesEsportivasCache = [], atividadesPedagogicasCache = []) => {
  if (!user?.id || user?.role !== ROLES.MONITOR) {
    return false;
  }

  if (!turmas || turmas.length === 0) {
    return false;
  }

  // Itera sobre todas as turmas do monitor
  const userMonitorId = String(user.id || user.user_id);


  for (const turma of turmas) {
    const horariosConfig = turma.horarios_config;

    if (!horariosConfig) {
      continue;
    }

    // Se horarios_config é string, tenta fazer parse
    let configParsed = horariosConfig;
    if (typeof horariosConfig === "string") {
      try {
        configParsed = JSON.parse(horariosConfig);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[verificarVinculacaoAtividade] Erro ao fazer parse do horarios_config:', e);
        }
        continue;
      }
    }

    if (typeof configParsed !== "object" || !configParsed) {
      continue;
    }

    for (const diaSemana in configParsed) {
      const horariosDoDia = configParsed[diaSemana];
      if (Array.isArray(horariosDoDia)) {
        for (const horario of horariosDoDia) {
          // Verifica se o monitor_id corresponde ao usuário logado (normaliza tipos)
          const horarioMonitorId = horario.monitor_id || horario.monitorId;

          if (!horarioMonitorId) {
            continue;
          }

          if (String(horarioMonitorId) === userMonitorId && horario.atividade_id) {
            const atividadeId = String(horario.atividade_id).toUpperCase();
            const isPedagogica = isAtividadePedagogica(atividadeId, atividadesPedagogicasCache);
            const isEsportiva = isAtividadeEsportiva(atividadeId, atividadesEsportivasCache);

            // Verifica se a atividade é pedagógica OU esportiva
            if (isPedagogica || isEsportiva) {
              return true;
            } else if (process.env.NODE_ENV === 'development') {
              console.warn('[verificarVinculacaoAtividade] Atividade encontrada mas não reconhecida como pedagógica ou esportiva:', {
                atividadeId,
                atividadeIdOriginal: horario.atividade_id,
                atividadesPedagogicasCacheIds: atividadesPedagogicasCache.slice(0, 5).map(a => String(a.id || a.atividade_id).toUpperCase()),
                atividadesEsportivasCacheIds: atividadesEsportivasCache.slice(0, 5).map(a => String(a.id || a.atividade_id).toUpperCase())
              });
            }
          }
        }
      }
    }
  }

  return false;
};

/**
 * Hook para verificar permissão de avaliações para monitor
 * @returns {Object} { hasPermission, loading }
 */
export const useMonitorAvaliacoesPermission = () => {
  const { user } = useAuth();
  const { fetchTurmas } = useTurmas();
  const [hasPermission, setHasPermission] = useState(false);
  const [loading, setLoading] = useState(true);

  const verificarPermissao = useCallback(async () => {
    // Se não for MONITOR, usa a verificação padrão de roles
    if (!user || user.role !== ROLES.MONITOR) {
      setHasPermission(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Busca atividades esportivas do banco para cache
      let atividadesEsportivasCache = [];
      try {
        const atividadesEsportivas = await atividadesEsportivasService.list({
          filters: { ativa: "eq.true" },
          limit: 1000
        });
        atividadesEsportivasCache = Array.isArray(atividadesEsportivas) ? atividadesEsportivas : [];
      } catch (err) {
        console.warn("Erro ao buscar atividades esportivas para verificação de permissão:", err);
        // Continua sem cache, usa apenas constante
      }

      // Busca atividades pedagógicas do banco para cache
      let atividadesPedagogicasCache = [];
      try {
        const atividadesPedagogicas = await atividadesPedagogicasService.list({
          limit: 1000
        });
        atividadesPedagogicasCache = Array.isArray(atividadesPedagogicas) ? atividadesPedagogicas :
          (atividadesPedagogicas?.data || []);
      } catch (err) {
        console.warn("Erro ao buscar atividades pedagógicas para verificação de permissão:", err);
        // Continua sem cache, usa apenas constante
      }

      // Busca todas as turmas ativas
      const turmas = await fetchTurmas({ limit: 1000 });
      const turmasArray = Array.isArray(turmas) ? turmas : turmas?.turmas || [];

      // Filtra apenas turmas do monitor
      const userMonitorId = String(user.id || user.user_id);
      const turmasDoMonitor = turmasArray.filter(turma => {
        // Verifica monitor_id direto na turma (normaliza tipos)
        const turmaMonitorId = turma.monitor_id || turma.monitorId;
        if (turmaMonitorId) {
          if (String(turmaMonitorId) === userMonitorId) {
            return true;
          }
        }

        // Verifica se o monitor está vinculado à turma através de horarios_config
        if (turma.horarios_config) {
          let horariosConfig = turma.horarios_config;
          if (typeof horariosConfig === "string") {
            try {
              horariosConfig = JSON.parse(horariosConfig);
            } catch (e) {
              // Se falhar ao fazer parse, continua sem essa turma
              return false;
            }
          }

          if (horariosConfig && typeof horariosConfig === "object") {
            for (const diaSemana in horariosConfig) {
              const horariosDoDia = horariosConfig[diaSemana];
              if (Array.isArray(horariosDoDia)) {
                for (const horario of horariosDoDia) {
                  const horarioMonitorId = horario.monitor_id || horario.monitorId;
                  if (horarioMonitorId && String(horarioMonitorId) === userMonitorId) {
                    return true;
                  }
                }
              }
            }
          }
        }

        return false;
      });

      // Verifica se há atividades pedagógicas OU esportivas vinculadas
      const temPermissao = verificarVinculacaoAtividade(user, turmasDoMonitor, atividadesEsportivasCache, atividadesPedagogicasCache);


      setHasPermission(temPermissao);
    } catch (error) {
      console.error("Erro ao verificar permissão de avaliações:", error);
      setHasPermission(false);
    } finally {
      setLoading(false);
    }
  }, [user, fetchTurmas]);

  useEffect(() => {
    verificarPermissao();
  }, [verificarPermissao]);

  return { hasPermission, loading };
};

export default useMonitorAvaliacoesPermission;

